import { ElMessage, ElMessageBox, FormInstance } from "element-plus";
import ajax from "@/http/index";
import DbHelper from "@/untils/BLL";
import Sortable from 'sortablejs';
import { Store, useStore } from 'vuex';
import JSEncrypt from 'jsencrypt';
import { useDebugLogger } from "@/untils/debugger";
import { validate as uuidValidate } from 'uuid';
import { uuid } from "vue3-uuid";
import { getCurrentInstance, onMounted, onUnmounted } from "vue";

// 1. 定义路由配置项的类型
interface RouteConfig {
  handler: (options: queryOptions | Array<any>, config?:any) => Promise<unknown|any>;
  url: string;
}

// 2. 定义路由组的类型（例如 secure 组，sms 组）
type RouteGroup = Record<string, RouteConfig>;

// 3. 定义整个路由表的类型
export type RouteMap = Record<string, RouteGroup>;

/**
 * 文件类型
 */
export enum fileTypes {
    login = '/storage/uploadfiles/sys/login-image.jpg',
    wxofficial = '/storage/uploadfiles/sys/wx-official.png',
    wxmini = '/storage/uploadfiles/sys/wx-mini.png'
}

/**
 * 接送机/站类型
 */
export enum FerryType
{
    delivery = '送机/送站',
    pickup = '接机/接站',
    unknow = '未知',
}

/**
 * 路径类型
 */
export enum PathType
{
    /** 市内 */ InCity,
    /** 市郊 */ Suburb,
    /** 跨市 */ TransCity,
    /** 跨省 */ TransProvincial,
    /** 跨国 */ TransNational,
    /** 未知 */ None,
}

/**
 * GPS项目运营类型
 */
export enum OperationType
{
  /** 双休 */ twodayoff = 2,
  /** 单休 */ onedayoff = 1,
  /** 全天 */ allday = 0,
  /** 手动 */ manualday = -1,
}

export const OperationTypeKeys = [
  "twodayoff", 
  "onedayoff", 
  "allday", 
  "manualday"
] as const;

/**
 * 天地图远程请求类型
 */
export enum TMapType {
    /** 地理信息 */ address = 'getAddress',
    /** 驾驶规划 */ drive = 'getDrive'
}

/**
 * 天地图类型
 */
export enum MapType {
    /** 未知 */ none = 'none',
    /** 驾驶专用 */ drive = 'drive',
    /** 驾驶规划 */ driving = 'driving',
    /** 轨迹 */ cartrack = 'cartrack',
    search = 'search',
    polygon = 'polygon',
}

/**
 * 天地图 标记类型
 */
export enum MARKTYPE {
    /** 起始 */ start = 'star',
    /** 途径 */ mid = 'mid',
    /** 终点 */ end = 'end',
    /** 车辆 */ bus = 'bus',
}

export interface Tree {
    label: string
    children?: Tree[]
}

/**
 * 远程请求参数
 */
export interface queryOptions {
    type?: string,
    url?: string,
    loading?: { 
        load: any,
        key: string
    } | boolean,

    /* 新增时使用 */
    form?: FormInstance|undefined,
    fields?: null,

    /* 删除时使用 */
    multiples?: Array<any>,
    hasAsk?:boolean,

    //装载数据接口
    params?: Record<string, any>,

    /* 自由使用 */
    [key:string] : any,
}

export const basePath = process.env.VUE_APP_BASE_URL;
const appRootPath = process.env.VUE_APP_BASE_API; //'/api';

export function createDebug(label?:string) {
    let logger: any = null;
    let objGroup:any = null;
    let componentName:string = 'UnknownComponent';

    onMounted(() => {
        // 获取组件名，如 'CarList' 或 'SysCar'，你可以自行处理截取逻辑
        const instance = getCurrentInstance();
        componentName = instance?.type?.name || 'UnknownComponent'; 
        logger = useDebugLogger(componentName);
        objGroup = logger.group(label ?? componentName);
    });

    onUnmounted(() => {
        if(objGroup!=null){
            objGroup.end();
        }

        if(logger!=null) {
            logger = null;
        }
  });

  return { 
    ...logger,
   };
}

export class Helper{
    static debug = useDebugLogger('Helper');

    static createDebug(moduleName: string) {
        const logger = useDebugLogger(moduleName);
        
        // 返回一个增强的对象，包含自动分组管理的逻辑
        return {
            ...logger,
            
            // 开启分组：返回一个包含 end 方法的对象
            startGroup: (label?: string) => {
                // 如果没传 label，默认使用 moduleName
                const groupLabel = label || moduleName;
                return logger.group(groupLabel);
            }
        };
    }

    static useDebugGroup(name?: string, options: { collapsed?: boolean } = {}) {
        // 1. 尝试获取当前实例，自动推断组件名
        const instance = getCurrentInstance();
        const componentName = name || instance?.type?.name || 'UnknownComponent';
        
        // 2. 创建专属 Logger
        const logger = Helper.createDebug(componentName);

        let groupEndHandler: {end: () => void} | null = null;

        // 3. 在 mounted 时自动开启分组
        onMounted(() => {
            // 开启分组，并保存 end 方法
            // options.collapsed 控制默认是否折叠 (true = 折叠, false = 展开)
            groupEndHandler = logger.group(componentName, options.collapsed);
            
            logger.log('>>> 组件挂载');
        });

        // 4. 在 unmounted 时自动关闭分组
        onUnmounted(() => {
            logger.log('<<< 生命周期结束');
            
            // 执行关闭
            if (groupEndHandler) {
                groupEndHandler.end();
            }
        });

        // 5. 返回 logger 供组件内部使用
        return logger;
    }

    static readonly SocketPath = process.env.NODE_ENV === 'development' ? `${process.env.VUE_APP_SOCKET_URL}:${process.env.VUE_APP_SOCKET_PORT}` : process.env.VUE_APP_SOCKET_URL;

    static readonly encrypt = new JSEncrypt();

    static readonly BaseUrl = process.env.VUE_APP_BASE_URL;
    static readonly routes: RouteMap = {
      common: {
        all: { handler: DbHelper.all, url: `${appRootPath}/common/getparams`},
        area: { handler: DbHelper.get, url: `${appRootPath}/api/getarea`},
        serviceqr: { handler: DbHelper.get, url: `${appRootPath}/api/getserviceqr`},
        qr: { handler: DbHelper.get, url: `${appRootPath}/api/getqr`},
        robin: { handler: DbHelper.set, url: `${appRootPath}/api/robinscan`},
        check: { handler: DbHelper.set, url: `${appRootPath}/common/checkuser`},
        customer: { handler: DbHelper.get, url: `${appRootPath}/common/getcustomer`},
        stat: { handler: DbHelper.get, url: `${appRootPath}/common/getstatistics`},
        signin: { handler: DbHelper.set, url: `${appRootPath}/api/signin`},
      },
      secure: {
        set: { handler: DbHelper.set, url: `${appRootPath}/api/secure`}
      },
      sms: {
        get: { handler: DbHelper.get, url: `${appRootPath}/api/getSms`},
        set: { handler: DbHelper.set, url: `${appRootPath}/api/setSms`},
        send: { handler: DbHelper.set, url: `${appRootPath}/api/sendSms`}
      },
      mail:{
        send: { handler: DbHelper.set, url: `${appRootPath}/common/sendmail`}
      },
      TMap: {
        address: { handler: DbHelper.get, url: `${appRootPath}/common/getlocation`},
        drive: { handler: DbHelper.set, url: `${appRootPath}/common/getdrive`},
        geocode : { handler: DbHelper.get, url: `${appRootPath}/common/getgeocode`}
      },
      gpsgroup: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getgpsgroup`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setgpsgroup`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setgpsgroup`},
      },
      GPS:{
        plateData: { handler: DbHelper.get, url: `${appRootPath}/common/getplatedata`},

        device: { handler: DbHelper.get, url: `${appRootPath}/api/gps`},
        getTrack: { handler: DbHelper.get, url: `${appRootPath}/common/getcartrack`},
        setTrack: { handler: DbHelper.get, url: `${appRootPath}/common/setcartrack`},

      },
      device: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getdevicelist`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setdevice`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setdevice`},
      },
      invoice:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getinvoice`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setinvoice`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setinvoice`},
      },
      order: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getorder`},
        params: { handler: DbHelper.get, url: `${appRootPath}/common/getorderparams`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setorder`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setorder`},
        export: { handler: DbHelper.download, url: `${appRootPath}/common/exportorder`},
      },
      orderDetails: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getorderdetails`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setorderdetails`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setorderdetails`},
        reconciliation: { handler: DbHelper.get, url: `${appRootPath}/common/getreconciliation`},
      },
      orderPaths: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getorderpaths`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setorderpaths`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setorderpaths`},
      },
      orderFee: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getorderfee`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setorderfee`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setorderfee`},
        verify: { handler: DbHelper.set, url: `${appRootPath}/common/getverify`},
      },
      hedge: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/gethedge`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/sethedge`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/sethedge`},

        enterprise: { handler: DbHelper.get, url: `${appRootPath}/common/gethedgeenterprise`},
        search: { handler: DbHelper.get, url: `${appRootPath}/common/gethedgedata`},

        setPlan: { handler: DbHelper.set, url: `${appRootPath}/common/sethedgeplan`},
        getPlan: { handler: DbHelper.get, url: `${appRootPath}/common/gethedgeplan`},
        export: { handler: DbHelper.download, url: `${appRootPath}/common/exportplan`},
      },
      member:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getmember`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setmember`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setmember`},
        login: { handler: DbHelper.set, url: `${appRootPath}/common/setMemberLogin`},
        signup: { handler: DbHelper.set, url: `${appRootPath}/common/setMemberSignup`},
      },
      staff:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getstaff`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setstaff`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setstaff`},
        //login: { handler: DbHelper.set, url: `${appRootPath}/common/syslogin`},
      },
      commission: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getcommission`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setcommission`},
        export: { handler: DbHelper.download, url: `${appRootPath}/common/exportcommission`},
      },
      driver:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getdriver`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setdriver`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setdriver`},
        operation: { handler: DbHelper.get, url: `${appRootPath}/common/getoperationdriver`},
      },
      enterprise:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getenterprise`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setenterprise`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setenterprise`},
      },
      client: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getclient`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setclient`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setclient`},
      },
      car: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getcar`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setcar`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setcar`},
        operation: { handler: DbHelper.get, url: `${appRootPath}/common/getoperationcar`},
      },
      cartype:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getcartype`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setcartype`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setcartype`},
      },
      finance:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getfinance`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setfinance`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setfinance`},
        export: { handler: DbHelper.set, url: `${appRootPath}/common/exportfinance`},
      },
      config: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getconf`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setconf`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setconf`},
      },
      menu: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getmenu`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setmenu`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setmenu`},
      },
      group: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getgroup`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setgroup`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setgroup`},
      },
      role:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getrole`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setrole`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setrole`},
      },
      userRole:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getuserrole`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setuserrole`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setuserrole`},
      },
      rights:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getrights`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setrights`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setrights`},
        menuTree: { handler: DbHelper.get, url: `${appRootPath}/common/getmenu`},
      },
      roleRights:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getrolerights`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setrolerights`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setrolerights`},
      },
      service:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getservice`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setservice`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setservice`},
      },
      serviceDetails:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getservicedetails`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setservicedetails`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setservicedetails`},
      },
      insurance:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getinsurance`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setinsurance`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setinsurance`},
      },
      insuranceDetails:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getinsurancedetails`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setinsurancedetails`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setinsurancedetails`},
      },
      file:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getfile`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setfile`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setfile`},
        image: { handler: DbHelper.get, url: `${appRootPath}/common/getimg`},
        upload: { handler: DbHelper.set, url: `${appRootPath}/common/uploadfile`},
        download: { handler: DbHelper.set, url: `${appRootPath}/common/downloadfile`},
      },
      remind: {
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getremind`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setremind`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setremind`},
      },
      accident:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getaccident`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setaccident`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setaccident`},
      },
      history:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/gethistory`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/sethistory`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/sethistory`},
      },
      content:{
        get: { handler: DbHelper.get, url: `${appRootPath}/common/getcontent`},
        set: { handler: DbHelper.set, url: `${appRootPath}/common/setcontent`},
        delete: { handler: DbHelper.delete, url: `${appRootPath}/common/setcontent`},
      },

      /*
        pay: `${appRootPath}/payment/pay`,
        payment: `${appRootPath}/common/payment`,
        getStatic: `${appRootPath}/common/website/getstaticdata`,
        setData: `${appRootPath}/common/website/setdata`,
        getCarseat: `${appRootPath}/common/getcarseat`,
        getCarcost: `${appRootPath}/common/getcarcost`,
        setCarcost: `${appRootPath}/common/setcarcost`,
        getOrderOffset: `${appRootPath}/common/getorderoffset`,
        getPackage: `${appRootPath}/common/getcarcost`,
        
        // Web Management
        getQRInfo: `${appRootPath}/common/getQRInfo`,
        setCheckSignIn: `${appRootPath}/common/checkSignIn`,
        webManger: "/manager",
        webImagePath: `${appRootPath}/common/getimg`,
        */
    } as const;

    static readonly Urls = {
        'none': undefined,
        // 加解密安全通道
        getServeSecure: `${appRootPath}/api/secure`,
        
        // GPS
        getPlateData: `${appRootPath}/common/getplatedata`,
        getDevicelist: `${appRootPath}/common/getdevicelist`,
        getGpsgroup: `${appRootPath}/common/getgpsgroup`,
        setGpsgroup: `${appRootPath}/common/setgpsgroup`,

        // Web
        getSlideshow: `${appRootPath}/common/web/slideshow`,
        getOrdertypes: `${appRootPath}/common/web/ordertypes`,
        getFooter: `${appRootPath}/common/web/footer`,
        getArea: `${appRootPath}/common/getarea`,

        setMemberlogin: `${appRootPath}/common/setMemberLogin`,
        getGps: `${appRootPath}/common/gps`,
        getRemind: `${appRootPath}/common/getremind`,
        setRemind: `${appRootPath}/common/setremind`,
        getReconciliation: `${appRootPath}/common/getreconciliation`,
        setFinance: `${appRootPath}/common/setfinance`,
        getFinance: `${appRootPath}/common/getfinance`,
        exportFinance: `${appRootPath}/common/exportfinance`,
        exportCommission: `${appRootPath}/common/exportcommission`,
        getOrder: `${appRootPath}/common/getorder`,
        setOrder: `${appRootPath}/common/setorder`,
        getOrderdetails: `${appRootPath}/common/getorderdetails`,
        setOrderdetails: `${appRootPath}/common/setorderdetails`,
        getOrderpaths: `${appRootPath}/common/getorderpaths`,
        setOrderpaths: `${appRootPath}/common/setorderpaths`,
        exportOrder: `${appRootPath}/common/exportorder`,
        getHedge: `${appRootPath}/common/gethedge`,
        getVerify: `${appRootPath}/common/getverify`,
        setHistory: `${appRootPath}/common/sethistory`,
        pay: `${appRootPath}/payment/pay`,
        payment: `${appRootPath}/common/payment`,
        getQRCode: `${appRootPath}/common/getQRCode`,
        checkBind: `${appRootPath}/common/checkBind`,
        getStatic: `${appRootPath}/common/website/getstaticdata`,
        setData: `${appRootPath}/common/website/setdata`,
        getImg: `${appRootPath}/common/website/getimg`,
        checkUser: `${appRootPath}/common/checkuser`,
        getCustomer: `${appRootPath}/common/getcustomer`,
        downloadFile: `${appRootPath}/common/downloadfile`,
        getConfig: `${appRootPath}/common/getconf`,
        setConfig: `${appRootPath}/common/setconf`,
        getCarseat: `${appRootPath}/common/getcarseat`,
        getCarcost: `${appRootPath}/common/getcarcost`,
        setCarcost: `${appRootPath}/common/setcarcost`,
        getMenu: `${appRootPath}/common/getmenu`,
        setMenu: `${appRootPath}/common/setmenu`,
        setCarTrack: `${appRootPath}/common/setcartrack`,
        getCarTrack: `${appRootPath}/common/getcartrack`,
        getcartype: `${appRootPath}/common/getCarType`,
        setcartype: `${appRootPath}/common/setCarType`,
        getDevice: `${appRootPath}/api/gps`,
        sendmail: `${appRootPath}/api/sendmail`,
        getHistory: `${appRootPath}/common/gethistory`,
        getOperationCar: `${appRootPath}/common/getoperationcar`,
        getOperationDriver: `${appRootPath}/common/getoperationdriver`,
        getCommission: `${appRootPath}/common/getcommission`,
        setCommission: `${appRootPath}/common/setcommission`,
        getRights: `${appRootPath}/common/getrights`,
        setRights: `${appRootPath}/common/setrights`,
        getRole: `${appRootPath}/common/getrole`,
        setRole: `${appRootPath}/common/setrole`,
        getGroup: `${appRootPath}/common/getgroup`,
        setGroup: `${appRootPath}/common/setgroup`,
        getStaff: `${appRootPath}/common/getstaff`,
        setStaff: `${appRootPath}/common/setstaff`,
        getCar: `${appRootPath}/common/getcar`,
        setCar: `${appRootPath}/common/setcar`,
        getDriver: `${appRootPath}/common/getdriver`,
        setDriver: `${appRootPath}/common/setdriver`,
        getUserRoles: `${appRootPath}/common/getuserroles`,
        setUserRoles: `${appRootPath}/common/setuserroles`,
        getRoleRights: `${appRootPath}/common/getrolerights`,
        setRoleRights: `${appRootPath}/common/setrolerights`,
        getRightsByMenu: `${appRootPath}/common/getrightsbymenu`,
        getEnterprise: `${appRootPath}/common/getenterprise`,
        setEnterprise: `${appRootPath}/common/setenterprise`,
        getService: `${appRootPath}/common/getservice`,
        setService: `${appRootPath}/common/setservice`,
        getServiceDetails: `${appRootPath}/common/getservicedetails`,
        setServiceDetails: `${appRootPath}/common/setservicedetails`,
        getInsurance: `${appRootPath}/common/getinsurance`,
        setInsurance: `${appRootPath}/common/setinsurance`,
        getInsuranceDetails: `${appRootPath}/common/getinsurancedetails`,
        setInsuranceDetails: `${appRootPath}/common/setinsurancedetails`,
        getAccident: `${appRootPath}/common/getaccident`,
        setAccident: `${appRootPath}/common/setaccident`,
        getContent: `${appRootPath}/common/getcontent`,
        setContent: `${appRootPath}/common/setcontent`,
        getMember: `${appRootPath}/common/getmember`,
        setMember: `${appRootPath}/common/setmember`,
        getOrderParams: `${appRootPath}/common/getorderparams`,
        getOrderOffset: `${appRootPath}/common/getorderoffset`,
        getFee: `${appRootPath}/common/getorderfee`,
        setFee: `${appRootPath}/common/setorderfee`,
        getFile: `${appRootPath}/common/getfile`,
        setFile: `${appRootPath}/common/setfile`,
        uploadfile: `${appRootPath}/common/uploadfile`,
        getStatistics: `${appRootPath}/common/getstatistics`,
        getPackage: `${appRootPath}/common/getcarcost`,
        getClient: `${appRootPath}/common/getclient`,
        setClient: `${appRootPath}/common/setclient`,
        getImage: `${appRootPath}/common/getimg`,
        
        // Web Management
        getQRInfo: `${appRootPath}/common/getQRInfo`,
        sysLogin: `${appRootPath}/common/syslogin`,
        setCheckSignIn: `${appRootPath}/common/checkSignIn`,
        webManger: "/manager",
        webImagePath: `${appRootPath}/common/getimg`,
        getTest: `${appRootPath}/common/test`
    } as const;

    // 调试工具
    static Debug(args: any, tag: string = 'DEBUG'): void {
        Helper.debug.log(tag, args);
    }

    static readonly Patterns = {
        // 基础验证
        VALID_PASSWORD: /^[a-zA-Z0-9_]\w{5,19}$/,
        VALID_MOBILE: /^1[3-9]\d{9}$/,
        VALID_EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        VALID_IDENTITY: /^[1-9]\d{5}(18|19|2[0-9])\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/,
        VALID_NUMPLATE: /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领][A-HJ-NP-Z][A-HJ-NP-Z0-9]{4,5}[A-HJ-NP-Z0-9挂学警港澳]$/,
        VALID_NUMBER: /^[+-]?(\d+(\.\d{1,2})?|\.\d{1,2})$/,
        
        // 汽车行业专用
        VALID_VIN: /^[A-HJ-NPR-Z0-9]{17}$/, // 车辆识别码
        VALID_ENGINE_NUMBER: /^[A-Z0-9]{6,12}$/,// 发动机号
        VALID_LICENSE_PLATE: /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领][A-HJ-NP-Z][A-HJ-NP-Z0-9]{4,5}[A-HJ-NP-Z0-9挂学警港澳]$/,
        VALID_CHASSIS_NUMBER: /^[A-Z0-9]{17}$/, // 车架号
        VALID_INSURANCE_NUMBER: /^[A-Z0-9]{10,20}$/,// 保险单号
        VALID_MILEAGE: /^\d{1,6}(\.\d{1,2})?$/,// 里程数
        VALID_VEHICLE_MODEL: /^[A-Z0-9-]{3,20}$/,// 车型代码
        VALID_FUEL_TYPE: /^(92|95|98|0|D|E|H|P)$/,// 燃油类型
        VALID_CAR_COLOR: /^(黑|白|灰|银|蓝|红|棕|绿|黄|紫|橙|金|粉)$/, // 车辆颜色
    }

    /**
     * 【重载 1】没有提供默认值 -> 返回值可能是 null
     */
    static getVal<T>(value: T | null | undefined): T | null;
    static getVal<T>(value: T | null | undefined, defaultValue: undefined | null): T | null;

    /**
     * 【重载 2】提供了非空默认值 -> 返回值肯定不是 null
     */
    static getVal<T>(value: T | null | undefined, defaultValue: T): T;

    /**
     * 安全获取值，避免 undefined/null/空字符串/空对象/空数组
     * 
     * @param value 输入值
     * @param defaultValue 默认值 (默认为 null)
     * @param strictObjectCheck 是否严格检查空对象 (默认 true，即 {} 视为无效值)
     * @returns 安全的值或默认值
     */
    static getVal<T>(value: T | undefined | null, defaultValue: T | null = null, strictObjectCheck: boolean = true): T | null {
        // 1. 处理 null 和 undefined
        if (value === undefined || value === null) {
            return defaultValue;
        }

        // 2. 处理字符串：去除空格后为空
        if (typeof value === 'string' && value.trim() === '') {
            return defaultValue;
        }

        // 3. 处理数组：长度为 0
        if (Array.isArray(value) && value.length === 0) {
            return defaultValue;
        }

        // 4. 【新增】处理数字：检查是否为 NaN
        // 注意：typeof NaN === 'number' 是 true，所以要单独判断
        if (typeof value === 'number' && Number.isNaN(value)) {
            return defaultValue;
        }

        // 5. 处理对象：是否检查空对象
        if (strictObjectCheck && typeof value === 'object' && value !== null) {
            // 排除数组（因为上面已经处理了数组）和 null（上面也处理了）
            if (!Array.isArray(value)) {
                // 检查对象是否没有任何自身属性
                if (Object.keys(value).length === 0) {
                    return defaultValue;
                }
            }
        }

        // 6. 其他情况视为有效值
        return value;
    }
    
    /**
     * 判断是否为纯对象（非数组、非null）
     * @param item 待检查项
     * @returns 是否为纯对象
     */
    static isJson(item: any): boolean {
        return typeof item === 'object' && item !== null && 
               !Array.isArray(item) && 
               Object.getPrototypeOf(item) === Object.prototype;
    }

    /**
     * 验证是否为有效的UUID/GUID
     * @param id 待验证字符串
     * @returns 是否为有效的UUID
     */
    static isGuid(id: string): boolean {
        if (!id || typeof id !== 'string') return false;
        
        // 标准UUID格式：xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        // 无分隔符格式：xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
        const compactRegex = /^[0-9a-f]{32}$/i;
        
        return uuidRegex.test(id) || compactRegex.test(id) || uuidValidate(id);
    }
    
    /**
     * 检查是否为有效数字
     * @param value 待检查值
     * @returns 是否为有效数字
     */
    static isNumber(value: unknown): boolean {
        if (typeof value === 'number') return !isNaN(value);
        if (typeof value === 'string') {
            const num = Number(value);
            return !isNaN(num) && value.trim() !== '';
        }
        return false;
    }

    /**
     * 验证手机号格式
     * @param mobile 手机号
     * @returns 是否为有效手机号
     */
    static isMobile(mobile: string): boolean {
        if (!mobile) return false;
        return Helper.Patterns.VALID_MOBILE.test(mobile.trim());
    }

    /**
     * 生成唯一ID
     * @param prefix 前缀
     * @returns 唯一ID字符串
     */
    static generateUniqueId(prefix: string = '', from: 'time' | 'uuid' = 'time'): string {
        if (from === 'time') {
            const timestamp = Date.now().toString(36);
            const random = Math.random().toString(36).substring(2, 9);
            return `${prefix}${timestamp}${random}`;
        } else if (from === 'uuid') {
            return uuid.v4();
        }
        return '';
    }

    /**
    * 生成随机字符串
    * @param length 字符串长度
    * @param charset 字符集
    * @returns 随机字符串
    */
    static generateRandomString(
        length: number = 8,
        charset: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    ): string {
        let result = '';
        const charsetLength = charset.length;
        
        for (let i = 0; i < length; i++) {
            result += charset.charAt(Math.floor(Math.random() * charsetLength));
        }
        
        return result;
    }
    /**
     * 类型安全的 Object.keys，保留枚举的键类型
     */
    static typedObjectKeys<T extends object>(obj: T): Array<keyof T> {
        return Object.keys(obj) as Array<keyof T>;
    }

    /**
     * 密码复杂度检查
     * @param password 密码
     * @param options 配置选项
     * @returns 错误信息（空字符串表示通过）
     */
    static checkPasswordComplexity(
        password: string, 
        options: {
            minLength?: number;
            requireUppercase?: boolean;
            requireLowercase?: boolean;
            requireNumbers?: boolean;
            requireSpecialChars?: boolean;
        } = {}
    ): string {
        const {
            minLength = 8,
            requireUppercase = true,
            requireLowercase = true,
            requireNumbers = true,
            requireSpecialChars = true
        } = options;

        if (!password || password.length < minLength) {
            return `密码长度至少为${minLength}位`;
        }

        const checks = [
            { condition: requireUppercase, test: /[A-Z]/, message: '需要包含大写字母' },
            { condition: requireLowercase, test: /[a-z]/, message: '需要包含小写字母' },
            { condition: requireNumbers, test: /\d/, message: '需要包含数字' },
            { condition: requireSpecialChars, test: /[\W_]/, message: '需要包含特殊字符' }
        ];

        for (const check of checks) {
            if (check.condition && !check.test.test(password)) {
                return check.message;
            }
        }

        return '';
    }

    /**
     * 获取字符串长度（中文算2个字符）
     * @param str 输入字符串
     * @returns 计算后的长度
     */
    static getStringLength(str: string): number {
        if (!str) return 0;
        return str.replace(/[^\x00-\xff]/g, '**').length;
    }

    /**
     * 格式化文件大小
     * @param bytes 字节数
     * @param options 格式化选项
     * @returns 格式化后的文件大小字符串
     */
    static formatFileSize(
        bytes: number, 
        options: {
            decimalPlaces?: number;
            units?: ('B' | 'KB' | 'MB' | 'GB' | 'TB')[];
            separator?: string;
        } = {}
    ): string {
        const {
            decimalPlaces = 1,
            units = ['B', 'KB', 'MB', 'GB', 'TB'],
            separator = ' '
        } = options;

        if (bytes === 0) return `0${separator}${units[0]}`;
        
        const k = 1024;
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const unit = units[Math.min(i, units.length - 1)];
        const size = bytes / Math.pow(k, i);
        
        return `${size.toFixed(decimalPlaces)}${separator}${unit}`;
    }

    /**
     * 深度查找数组中的元素
     * @param arr 目标数组
     * @param predicate 查找条件函数
     * @returns 找到的元素或null
     * 
     * -- 示例：
     * const a:any = deepFind([{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }], item => item.id === 2);
     */
    static deepFind<T>(
        arr: T[], 
        predicate: (item: T) => boolean
    ): T | null {
        if (!Array.isArray(arr)) return null;
        
        for (const item of arr) {
            if (predicate(item)) return item;
            
            if (item && typeof item === 'object') {
                for (const key in item) {
                    if (Object.prototype.hasOwnProperty.call(item, key)) {
                        const val = item[key];
                        if (Array.isArray(val)) {
                            const result = Helper.deepFind(val, predicate);
                            if (result) return result;
                        }
                    }
                }
            }
        }
        
        return null;
    }

    /**
     * 计算百分比（保留指定小数位数）
     * @param value 原始值
     * @param total 总值
     * @param decimalPlaces 小数位数
     * @returns 百分比数值
     */
    static calculatePercentage(
        value: number, 
        total: number, 
        decimalPlaces: number = 2
    ): number {
        if (total === 0) return 0;
        const percentage = (value / total) * 100;
        return Number(percentage.toFixed(decimalPlaces));
    }

    /**
     * 截断字符串（考虑中文字符）
     * @param str 原始字符串
     * @param maxLength 最大长度（中文算2个字符）
     * @param suffix 截断后缀
     * @returns 截断后的字符串
     */
    static truncateString(
        str: string, 
        maxLength: number, 
        suffix: string = '...'
    ): string {
        if (!str || maxLength <= 0) return '';
        
        let length = 0;
        let index = 0;
        
        while (index < str.length) {
            const charCode = str.charCodeAt(index);
            const charLength = charCode > 255 ? 2 : 1;
            
            if (length + charLength > maxLength) break;
            
            length += charLength;
            index++;
        }
        
        return str.slice(0, index) + (index < str.length ? suffix : '');
    }
    /**
     * 格式化金额
     * @param amount 金额数值
     * @param options 格式化选项
     * @returns 格式化后的金额字符串
     */
    static formatCurrency(
        amount: number | string,
        options: {
            decimalPlaces?: number;
            decimalSeparator?: string;
            thousandSeparator?: string;
            currencySymbol?: string;
            symbolPosition?: 'prefix' | 'suffix';
            emptyPlaceholder?: string; // 新增：空值时的占位符，如 '--'
        } = {}
    ): string {
        const {
            decimalPlaces = 2,
            decimalSeparator = '.',
            thousandSeparator = ',',
            currencySymbol = '￥',
            symbolPosition = 'prefix',
            emptyPlaceholder = '--' // 默认空值显示 --
        } = options;

        // 1. 使用 Helper 清洗数据，无效值统一转为空字符串 ''
        // 注意：这里假设 Helper.getVal 在遇到 null/undefined/NaN/空串时返回 defaultValue ('')
        const safeAmount = Helper.getVal(amount, '');

        // 2. 如果清洗后是空字符串，直接返回占位符（不显示 ￥0.00）
        if (safeAmount === '') {
            return currencySymbol ? `${currencySymbol}${emptyPlaceholder}` : emptyPlaceholder;
        }

        let num: number;

        // 3. 根据类型分别处理
        if (typeof safeAmount === 'string') {
            // 字符串转数字
            // parseFloat 会忽略尾部非数字字符，但头部必须合法
            // 例如 "19.90px" -> 19.9, "abc" -> NaN
            num = parseFloat(safeAmount);
        } else {
            // 数字类型（通常是计算结果或已转换的值）
            num = safeAmount as number;
        }

        // 4. 二次校验：防止 parseFloat("abc") 产生 NaN
        if (isNaN(num) || !isFinite(num)) {
            return currencySymbol ? `${currencySymbol}${emptyPlaceholder}` : emptyPlaceholder;
        }

        // 5. 精度修正：消除浮点误差
        // 即使是字符串转来的，也可能有精度问题（极少），或者数字类型本身就有误差
        num = Math.round(num * 100) / 100;
        
        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
            useGrouping: true
        });
        
        let formatted = formatter.format(num)
            .replace(/,/g, 'TEMP_THOUSAND')
            .replace(/\./g, decimalSeparator)
            .replace(/TEMP_THOUSAND/g, thousandSeparator);
        
        if (currencySymbol) {
            formatted = symbolPosition === 'prefix' 
                ? `${currencySymbol}${formatted}` 
                : `${formatted}${currencySymbol}`;
        }
        
        return formatted;
    }

    /**
     * 格式化数字
     * @param number 
     * @param decimalPlaces 
     * @param decimalSeparator 
     * @param thousandSeparator 
     * @returns 
     */

    static amount(number: any, decimalPlaces: number = 2, decimalSeparator: string = '.', thousandSeparator: string = ','): string {
        const num = isNaN(number) ? 0 : Number(number);
        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
            useGrouping: true
        });
        return formatter.format(num)
            .replace(/,/g, 'TEMP_THOUSAND')
            .replace(/\./g, decimalSeparator)
            .replace(/TEMP_THOUSAND/g, thousandSeparator);
    }
    
    static formatDateTime(date: string, format?: string): string;
    static formatDateTime(date: number, format?: string): string;
    static formatDateTime(date: Date, format?: string): string;
    /**
     * 格式化日期时间
     * @param date 日期值
     * @param format 格式字符串
     * @returns 格式化后的日期字符串
     */
    static formatDateTime(
        date: string | number | Date,
        format: string = 'yyyy-MM-dd'
    ): string {
        if (Helper.getVal(date)==null) return '';
        
        const d = new Date(
            typeof date === 'string' 
                ? date.replace(/-/g, '/').replace(/T/, ' ') 
                : date
        );
        
        if (isNaN(d.getTime())) return date.toString();
        
        const pad = (n: number) => n.toString().padStart(2, '0');
        const pad3 = (n: number) => n.toString().padStart(3, '0');
        
        const tokens: Record<string, string> = {
            'YYYY': d.getFullYear().toString(),
            'yyyy': d.getFullYear().toString(),
            'yy': d.getFullYear().toString().slice(-2),
            'MM': pad(d.getMonth() + 1),
            'M': (d.getMonth() + 1).toString(),
            'DD': pad(d.getDate()),
            'dd': pad(d.getDate()),
            'd': d.getDate().toString(),
            'HH': pad(d.getHours()),
            'H': d.getHours().toString(),
            'hh': pad(d.getHours() % 12 || 12),
            'h': (d.getHours() % 12 || 12).toString(),
            'mm': pad(d.getMinutes()),
            'm': d.getMinutes().toString(),
            'ss': pad(d.getSeconds()),
            's': d.getSeconds().toString(),
            'SSS': pad3(d.getMilliseconds()),
            'a': d.getHours() < 12 ? 'AM' : 'PM',
            'A': d.getHours() < 12 ? '上午' : '下午'
        };
        
        return format.replace(/(YYYY|yyyy|yy|MM?|DD?|dd?|HH?|hh?|ss?|mm?|SSS|a|A)/g, 
            (match) => tokens[match] || match);
    }
    /**
     * 生成指定时间范围内的日期数组
     * @param options 配置选项
     * @returns 日期字符串数组
     */
    static generateDateRange(options: {
        startDate: string | Date;
        endDate?: string | Date;
        type?: 'day' | 'hour' | 'week' | 'month';
        format?: string;
        step?: number;
    }): string[] {
        const {
            startDate,
            endDate = new Date(),
            type = 'day',
            format = 'YYYY-MM-DD',
            step = 1
        } = options;
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
        
        const result: string[] = [];
        let current = new Date(start);
        
        while (current <= end) {
            result.push(Helper.formatDateTime(current, format));
            
            switch (type) {
                case 'hour':
                    current.setHours(current.getHours() + step);
                    break;
                case 'day':
                    current.setDate(current.getDate() + step);
                    break;
                case 'week':
                    current.setDate(current.getDate() + 7 * step);
                    break;
                case 'month':
                    current.setMonth(current.getMonth() + step);
                    break;
            }
        }
        
        return result;
    }

    static insertCharAt(str:string, pos:number = 2, c:string = ' '){
        return str.slice(0, pos) + c + str.slice(pos)
    }

    /**
     * 格式化时间间隔
     * @param seconds 秒数
     * @param options 格式化选项
     * @returns 格式化后的时间字符串
     */
    static formatDuration(
        seconds: number,
        options: {
            units?: ('d' | 'h' | 'm' | 's')[];
            showEmptyUnits?: boolean;
            separator?: string;
            locale?: 'zh' | 'en';
        } = {}
    ): string {
        const {
            units = ['d', 'h', 'm', 's'],
            showEmptyUnits = false,
            separator = ' ',
            locale = 'zh'
        } = options;
        
        const timeUnits = {
            d: { value: 86400, zh: '天', en: 'd' },
            h: { value: 3600, zh: '小时', en: 'h' },
            m: { value: 60, zh: '分钟', en: 'm' },
            s: { value: 1, zh: '秒', en: 's' }
        };
        
        let remaining = Math.abs(seconds);
        const parts: string[] = [];
        
        for (const unit of units) {
            const unitConfig = timeUnits[unit];
            if (!unitConfig) continue;
            
            const value = Math.floor(remaining / unitConfig.value);
            remaining %= unitConfig.value;
            
            if (value > 0 || showEmptyUnits) {
                const suffix = locale === 'zh' ? unitConfig.zh : unitConfig.en;
                parts.push(`${value}${suffix}`);
            }
        }
        
        if (parts.length === 0) {
            const suffix = locale === 'zh' ? '0秒' : '0s';
            return suffix;
        }
        
        return parts.join(separator);
    }

    static loadScript(url: string|string[]) {
        let arr = [] as  string[];
        if(typeof url == 'string') {
            arr = [url];
        }else{
            arr = url;
        }

        return new Promise<void>((resolve, reject) => {
            arr.forEach(strUrl => {
                const script = document.createElement("script")
                script.src = strUrl
                script.type = "text/javascript"
                script.async = true
                script.defer = true
                script.onload = () => resolve()
                script.onerror = () => resolve() // 静默失败
                document.body.appendChild(script)
            });
        })
    }

    /**
     * 修复被动事件监听器（提升滚动性能）
     */
    static fixPassiveEvents(): void {
        if (typeof window === 'undefined') return;
        
        try {
            let supportsPassive = false;
            const opts = Object.defineProperty({}, 'passive', {
                get() {
                    supportsPassive = true;
                    return true;
                }
            });
            
            window.addEventListener('test', () => {}, opts);
            window.removeEventListener('test', () => {}, opts);
            
            if (!supportsPassive) return;
            
            const originalAddEventListener = EventTarget.prototype.addEventListener;
            const passiveEvents = ['wheel', 'touchstart', 'touchmove', 'scroll'];
            
            EventTarget.prototype.addEventListener = function(
                type: string,
                listener: EventListenerOrEventListenerObject,
                options?: boolean | AddEventListenerOptions
            ) {
                if (passiveEvents.includes(type)) {
                    const opts = typeof options === 'boolean' 
                        ? { capture: options, passive: true }
                        : typeof options === 'object'
                        ? { ...options, passive: options.passive !== false }
                        : { passive: true };
                    
                    return originalAddEventListener.call(this, type, listener, opts);
                }
                
                return originalAddEventListener.call(this, type, listener, options);
            };
        } catch (error) {
            Helper.Debug(error, 'fixPassiveEvents');
        }
    }

    static isUrlKey(key: string): key is keyof typeof Helper.Urls {
        return key in Helper.Urls;
    }
    
    /**
     * 获取API URL
     * @param key API键名
     * @returns API URL
     * @throws 当URL不存在时抛出错误
     */
    static getUrl(key:string|undefined, method:'get'|'set'='get'): string {
        if(key!=undefined && Helper.isUrlKey(key)){
            const url = Helper.Urls[key];
            if(url){
                return url;
            }
        }
        
        throw new Error(`API URL not found for key: ${key}`);
    }

    // 3. 获取URL的方法
    static getURL<
        T extends keyof typeof Helper.routes,
        M extends keyof typeof Helper.routes[T]
    >(resource: T, method: M): string {
      const res = Helper.routes[resource][method] as {
        handler: (options: queryOptions) => Promise<unknown>;
        url: string;
      };
        return res.url;
    }

    /**
     * 计算阶梯提成
     * @param sale 销售额
     * @param commissionRules 提成规则数组
     * @returns 提成金额
     */
    static calculateCommission(
        sale: number,
        commissionRules: Array<{
            threshold: number;
            rate: number;
            fixedAmount?: number;
        }>
    ): number {
        if (sale <= 0 || !Array.isArray(commissionRules) || commissionRules.length === 0) {
            return 0;
        }
        
        // 按阈值降序排序
        const sortedRules = [...commissionRules].sort((a, b) => b.threshold - a.threshold);
        let remainingSale = sale;
        let totalCommission = 0;
        
        for (let i = 0; i < sortedRules.length; i++) {
            const rule = sortedRules[i];
            const nextThreshold = i < sortedRules.length - 1 
                ? sortedRules[i + 1].threshold 
                : 0;
            
            if (remainingSale > rule.threshold) {
                const amountInThisTier = remainingSale - Math.max(rule.threshold, nextThreshold);
                
                if (rule.fixedAmount !== undefined) {
                    totalCommission += rule.fixedAmount;
                } else {
                    totalCommission += amountInThisTier * rule.rate;
                }
                
                remainingSale = rule.threshold;
            }
        }
        
        return Number(totalCommission.toFixed(2));
    }
    
    /**
     * 判断点是否在多边形内（射线法）
     * @param point 点坐标 [x, y]
     * @param polygon 多边形顶点数组 [[x1, y1], [x2, y2], ...]
     * @returns 是否在多边形内
     */
    static isPointInPolygon(
        point: [number, number],
        polygon: [number, number][]
    ): boolean {
        if (!point || !polygon || polygon.length < 3) return false;
        
        const [x, y] = point;
        let inside = false;
        
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];
            
            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            
            if (intersect) inside = !inside;
        }
        
        return inside;
    }

    /**
     * 数组元素重新排序
     * @param array 原始数组
     * @param fromIndex 移动元素的原始索引
     * @param toIndex 目标索引
     * @returns 重新排序后的新数组
     */
    static setOrder<T>(
        array: T[],
        options: { fromIndex: number; toIndex: number } | { index: number; direction: 'up'|'down' },
        deepClone = false
    ): T[] {
        // 输入校验
        if (!Array.isArray(array)) return [];
        
        // 深拷贝处理
        const result = deepClone ? JSON.parse(JSON.stringify(array)) : [...array];
        
        // 处理两种模式
        if ('fromIndex' in options) {
            const { fromIndex, toIndex } = options;
            if ([fromIndex, toIndex].some(i => i < 0 || i >= result.length)) return result;
            const [item] = result.splice(fromIndex, 1);
            result.splice(toIndex, 0, item);
        } else {
            const { index, direction } = options;
            const swapIndex = direction === 'up' ? index - 1 : index + 1;
            if (swapIndex < 0 || swapIndex >= result.length) return result;
            [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
        }

        return result;
    }

    /**
     * 生成图片URL
     * @param options 图片配置选项
     * @returns 完整的图片URL
     */
    static getImageUrl(options: {
        id: string;
        height?: number;
        from?: 'object' | 'self';
        mode?: number;
        noCache?: boolean;
        baseUrl?: string;
    }): string {
        const {
            id,
            height = 0,
            from = 'self',
            mode = 0,
            noCache = false,
            baseUrl = Helper.getURL('file', 'image')
        } = options;
        
        if (!id || !baseUrl) return '';

        let _url:string = baseUrl;
        if(process.env.NODE_ENV === 'development') {
            if(_url.indexOf('img') > -1) {
                _url = _url.replace('/api', process.env.VUE_APP_SOURCE_URL);
            }
        }
        
        const params = new URLSearchParams({
            h: height.toString(),
            [from === 'object' ? 'oid' : 'id']: id
        });
        
        if (noCache) {
            params.append('t', Date.now().toString());
        }
        
        return `${_url}?${params.toString()}`;
    }

    static reloadImage(url:string, obj:any){
      obj.src = `${url}?t=${new Date().getTime()}`;
    }

   /**
   * 深拷贝函数 (自动降级策略 + 开发日志)
   * 策略链: structuredClone -> 递归拷贝 -> JSON拷贝
   * 
   * @param source 需要拷贝的对象
   * @param strategy 策略索引 (内部使用)
   * @returns 拷贝后的副本
   */
   static Clone<T = any>(source: T, strategy: number = 0): T {
    // 0. 基础类型或 null/undefined，直接返回
    if (source === null || typeof source !== 'object') {
      return source;
    }

    // 0.1 日期对象
    if (source instanceof Date) {
      return new Date(source.getTime()) as any;
    }

    // 0.2 正则对象
    if (source instanceof RegExp) {
      return new RegExp(source.source, source.flags) as any;
    }

    // ---------------------------------------------------------
    // 策略 1: structuredClone (现代浏览器原生支持，性能极佳)
    // ---------------------------------------------------------
    if (strategy === 0) {
      if (typeof (window as any).structuredClone !== 'undefined') {
        try {
          const result = (window as any).structuredClone(source);
          return result;
        } catch (e) {
          Helper.debug.error('[Clone] structuredClone 失败，降级为递归拷贝', e);
          // 降级：递归调用自身，策略索引 +1
          return Helper.Clone(source, 1);
        }
      } else {
        Helper.debug.warn('[Clone] 浏览器不支持 structuredClone，降级为递归拷贝');
        return this.Clone(source, 1);
      }
    }

    // ---------------------------------------------------------
    // 策略 3: JSON.parse/stringify (最后的兜底方案)
    // ---------------------------------------------------------
    if (strategy === 2) {
      try {
        const result = JSON.parse(JSON.stringify(source));
        return result;
      } catch (e) {
        // 彻底失败：通常是循环引用
        this.debug.error('[Clone] JSON 拷贝 失败 (检测到循环引用)，返回原引用', e);
        return source as any; // 返回原引用，避免程序崩溃
      }
    }

    // 理论上不会走到这里
    return source;
  };

    /*
     * 改写用户权限
     * @param userRights 用户权限字符串
     * @param requiredRights 所需权限
    */
    static changeRights(userRights: string, rightsStructure: Record<string, boolean>) : Record<string, boolean> {
        if (userRights === 'system-all') {
            Object.keys(rightsStructure).forEach(key => {
                rightsStructure[key] = true;
            });
            return rightsStructure;
        }

        const normalizedRights = userRights.replace(/-/g, '_');
        Object.keys(rightsStructure).forEach(key => {
            rightsStructure[key] = normalizedRights.includes(key.replace(/-/g, '_'));
        });

        return rightsStructure;
    }

    // 声明拦截器数组
    private static requestInterceptors: Array<(options: queryOptions, conf?: any) => { options: queryOptions, conf?: any }> = [];
    private static responseInterceptors: Array<(response: any) => any> = [];

    /**
     * 添加请求拦截器
     * @param interceptor 
     * 
     * -示例
     * // 添加请求拦截器，例如添加 token
     * Helper.addRequestInterceptor((options, conf) => {
     *   const token = localStorage.getItem('token');
     *   if (token) {
     *     // 修改 conf 对象，添加认证头
     *     conf = {
     *       ...conf,
     *       headers: {
     *         ...conf?.headers,
     *         'Authorization': `Bearer ${token}`
     *       }
     *     };
     *   }
     *   return { options, conf };
     * });
     */
    static addRequestInterceptor(interceptor: (options: queryOptions, conf?: any) => { options: queryOptions, conf?: any }) {
      this.requestInterceptors.push(interceptor);
    }

    /**
     * 添加响应拦截器
     * @param interceptor 
     * 
     * -示例 
     * // 添加响应拦截器，例如处理错误
     * Helper.addResponseInterceptor((response) => {
     *   if (response.status === 401) {
     *     // 处理未授权情况
     *     localStorage.removeItem('token');
     *     window.location.href = '/login';
     *   }
     *   return response;
     * })
     */
    static addResponseInterceptor(interceptor: (response: any) => any) {
      // 保存拦截器
      this.responseInterceptors.push(interceptor);
    }
    
    /**
     * 执行远程请求
     * @param resource 请求路由键名
     * @param method 请求路由方法名
     * @param options 请求路由参数
     * @returns 
     */
    static exec<
        T extends keyof RouteMap,
        M extends keyof RouteMap[T]
    >(
        resource: T,
        method: M,
        options: queryOptions | Array<any>,
        conf?: any
    ) {
        if (!Helper.routes || !Helper.routes[resource] || !Helper.routes[resource][method]) {
            Helper.debug.error(`Invalid URL configuration for ${resource}.${method}`);
            return Promise.reject(new Error(`Invalid URL configuration for ${resource}.${method}`));
        }
        const config = Helper.routes[resource][method];

        
        // 如果 options 不是数组，则设置 url
        let processedOptions = options;
        let processedConf = conf;
        
        if (!Array.isArray(options)) {
          processedOptions = { ...options, url: config.url };
          // 应用请求拦截器
          for (const interceptor of this.requestInterceptors) {
            const result = interceptor(processedOptions, processedConf);
            processedOptions = result.options;
            processedConf = result.conf;
          }
        }
        
        return config.handler(processedOptions, processedConf)
          .then((response:any) => {
            // 应用响应拦截器
            let processedResponse = response;
            for (const interceptor of this.responseInterceptors) {
              processedResponse = interceptor(processedResponse);
            }
            return processedResponse;
          });

        //if(!Array.isArray(options)) options = { ...options, url: config.url };
        //return config.handler(options, conf);
    }

    // 创建便捷的请求方法
    /*
    // 使用示例
      const commonApi = Helper.createRequestHelper('common');

      // 使用快捷方法
      commonApi.area({ id: 1 }).then(res => {
        console.log('区域信息', res);
      });
    */
    // 创建请求辅助方法
    static createRequestHelper<T extends keyof RouteMap>(resource: T) {
      // 获取资源下的所有方法
      const methods = Object.keys(Helper.routes[resource]) as Array<keyof RouteMap[T]>;
      
      // 创建基础对象
      const helper: any = {
        exec: <M extends keyof RouteMap[T]>(
          method: M,
          options: queryOptions | Array<any>,
          conf?: any
        ) => Helper.exec(resource, method, options, conf),
        resource: resource as T,
      };
      
      // 为每个方法创建快捷方法
      methods.forEach(method => {
        helper[method] = (options: queryOptions | Array<any>, conf?: any) => 
          Helper.exec(resource, method, options, conf);
      });
      
      return helper as {
        resource: T;
        exec: <M extends keyof RouteMap[T]>(
          method: M,
          options: queryOptions | Array<any>,
          conf?: any
        ) => Promise<unknown>;
      } & {
        [M in keyof RouteMap[T]]: (
          options: queryOptions | Array<any>,
          conf?: any
        ) => Promise<unknown>;
      };
    }

    /**
     * 请求安全接口
     * @param options 参数
     * @returns 返回已解密的和未加解密的结果
     */
    static async requestSecure(options:any):Promise<any>{
      return await shareSystemCrypto.secureRequest(
        options
      );
    }

    static checkRights(options: { 
      userRights: string,
      rights: Record<string, boolean>, 
      rightskey?: string, 
      handler: string 
    }): boolean {
      // 如果用户权限为空字符串，则直接返回false
      if (options.userRights === '') {
        Helper.debug.warn('用户权限为空');
        return false;
      }
    
      // 超级管理员直接返回true
      if (options.userRights === 'system-all') return true;
    
      // 将用户权限和handler中的横线替换为下划线
      const _userRights = options.userRights.replace(/-/g, '_');
      const _handler = options.handler.replace(/-/g, '_');
    
      // 处理包含 && 或 || 的复杂权限表达式
      if (_handler.includes('&&') || _handler.includes('||')) {
        return this.evaluateComplexPermission(_handler, options, _userRights);
      }
    
      // 处理单个权限
      return this.checkSinglePermission(_handler, options, _userRights);
    }

    // 评估复杂权限表达式
    private static evaluateComplexPermission(
      handler: string, 
      options: { rights: Record<string, boolean>, rightskey?: string }, 
      userRights: string
    ): boolean {
      // 将表达式拆分为多个权限检查
      const expressions = handler.split(/(&&|\|\|)/);
      let result = true;
      let lastOperator = '&&';

      for (let i = 0; i < expressions.length; i++) {
        const expr = expressions[i].trim();
        
        if (expr === '&&' || expr === '||') {
          lastOperator = expr;
          continue;
        }

        const hasPermission = this.checkSinglePermission(expr, options, userRights);
        
        if (lastOperator === '&&') {
          result = result && hasPermission;
        } else {
          result = result || hasPermission;
        }
        
        // 短路优化
        if ((lastOperator === '&&' && !result) || (lastOperator === '||' && result)) {
          return result;
        }
      }

      return result;
    }

    // 检查单个权限
    private static checkSinglePermission(
      handler: string, 
      options: { rights: Record<string, boolean>, rightskey?: string }, 
      userRights: string
    ): boolean {
      // 检查用户是否有这个基础权限
      if (userRights.indexOf(handler) === -1) {
        return false;
      }

      // 获取权限的最后一部分（功能部分）
      const parts = handler.split('_');
      const lastPart = parts[parts.length - 1];
      
      // 如果有多级权限（如 sys_xxx_ddd_browse）
      if (parts.length > 1) {
        // 如果有rightskey，则使用它构建完整的权限键
        if (options.rightskey) {
          const fullKey = `${options.rightskey}_${lastPart}`;
          return options.rights[fullKey] ?? false;
        }
        
        // 否则直接使用handler作为键
        return options.rights[handler] ?? false;
      }

      // 如果只有一级权限（如 browse）
      if (options.rightskey) {
        const fullKey = `${options.rightskey}_${handler}`;
        return options.rights[fullKey] ?? false;
      }

      return options.rights[handler] ?? false;
    }

    /**
     * 查找节点的顶层父节点
     * 
     * @param nodes 树形数据
     * @param targetId 要查找的节点ID
     * @param options 配置选项
     * @returns 找到的顶层父节点，如果未找到则返回null
     */
    static findTopParentNode<T = any>(
        nodes: T[],
        targetId: string | number,
        options?: {
            /** 字段映射配置 */
            fieldMap?: {
                id?: string;
                children?: string;
                parentId?: string;
            };
        }
    ): T | null {
        if (!Array.isArray(nodes)) return null;

        // 默认字段映射配置
        const map = {
            id: 'id',
            children: 'children',
            parentId: 'parentId',
            ...options?.fieldMap
        };

        // 递归查找函数
        const findNode = (currentNodes: T[], parent: T | null = null): T | null => {
            for (const node of currentNodes) {
                // 如果当前节点是目标节点，返回其父节点
                if ((node as any)[map.id] === targetId) {
                    return parent;
                }

                // 递归查找子节点
                const children = (node as any)[map.children];
                if (children && children.length > 0) {
                    const found = findNode(children, node);
                    if (found) {
                        return found;
                    }
                }
            }
            return null;
        };

        return findNode(nodes);
    }
    /**
     * 【通用】过滤树结构（倒查逻辑）
     * 
     * @param nodes 原始节点数组
     * @param options 配置选项
     * @returns 过滤后的节点数组
     */
    static filterTree<T = any>(
        nodes: T[], 
        options: {
            /** 判断当前节点是否应该保留 */
            filterCallback: (node: T) => boolean;
            
            /** 判断当前节点是否是叶子节点 */
            isLeafCallback?: (node: T) => boolean;
            
            /** 字段映射配置 */
            fieldMap?: {
                id?: string;
                children?: string;
            };
        }
    ): T[] {
        if (!Array.isArray(nodes)) return [];

        const { filterCallback, isLeafCallback, fieldMap } = options;
        
        // 1. 默认字段映射
        const map = {
            id: fieldMap?.id ?? 'id',
            children: fieldMap?.children ?? 'children'
        };

        // 2. 递归处理
        const processNode = (currentNodes: T[]): T[] => {
            return currentNodes
                .map(node => {
                    // 2.1 获取子节点
                    const children = (node as any)[map.children];

                    // 2.2 判断是否是叶子节点
                    let isLeaf = false;
                    if (isLeafCallback) {
                        isLeaf = isLeafCallback(node);
                    } else {
                        const total = (node as any).total;
                        isLeaf = (total === 0 || total === '0');
                    }

                    if (isLeaf) {
                        // --- 【叶子节点逻辑】 ---
                        const shouldKeep = filterCallback(node);
                        // 【修改点】这里可能返回 null，所以用 as T 满足泛型约束
                        return shouldKeep ? node : (null as any as T);
                    } else {
                        // --- 【父节点逻辑】 ---
                        const filteredChildren = processNode(children || []);

                        if (filteredChildren.length > 0) {
                            return {
                                ...node,
                                [map.children]: filteredChildren
                            } as T; // 【修改点】断言返回类型符合 T
                        } else {
                            return null as any as T; // 【修改点】断言返回类型符合 T
                        }
                    }
                })
                .filter(node => node !== null) as T[]; // 【修改点】断言过滤后的数组是 T[]
        };

        return processNode(nodes);
    }
}

export class commonHelper {
    /**
     * 远程获取tk, 并解密
     */
    static getTokenKey = async () => {
        const res = await Helper.requestSecure({type: 'TiandituTK'});
        return res.tk;
    }

    static getExcel(option:queryOptions){
        option.type = 'getExcel';
        return DbHelper.download(option);
    }

    static checkBind (option:any){
        option.type = 'checkBind';
        return DbHelper.set(option);
    }

    static getQRCode(option:any){
        option.type = 'getQRCode';
        
        let _url:string = Helper.getUrl(option.type);

        option.url = _url.replace('php.jiatengbus.com', 'www.jiatengbus.com');
        return DbHelper.get(option);
    }

    static getGPS(option:any){
        option.type = 'getGps';
        return DbHelper.download(option);
    }

    static get(option:any) {
        return DbHelper.get(option);
    }

    static all(option:any){
        return DbHelper.all(option);
    }
    
    static set(option:any){
        return DbHelper.set(option);
    }
    
    static delete(option:any){
        return DbHelper.delete(option);
    }

    static checkUser(option:any){
        option.type = 'checkUser';
        return DbHelper.set(option);
    }

    static sort(options:{container:HTMLElement, list?:Array<any>, key?:string}, callback:any = null){
      Sortable.create(options.container, {
          dragClass: "sortable-drag",
          ghostClass: "sortable-ghost", 
          onEnd: (evt:any)=>{
              if(evt.oldIndex == evt.newIndex) return;
              if(options.list && options.key) {
                let _data = { 'key': options.list[evt.oldIndex][options.key], 'ordernum': evt.newIndex + 1 };
                if(callback!=null) callback(_data);
                return;
              }
              if(callback!=null) callback(evt);
          }
      });
    }

    static status(option:any, callback:any = null){
        if(option.statuslist == null|| option.statuslist.length == 0) {
            ElMessage.error({ message: '状态集合未找到', grouping: true});
            return;
        }

        console.log(option.statusvalue);

        let status:any = option.statuslist.find((item:any)=>{
            return item.groupvalue == option.statusvalue;
        });

        if(status == null) {
            ElMessage.error({ message: '状态未找到', grouping: true});
            return;
        }

        let _data = { 'id': option.order.id, 'statusid': status.id, 'statusvalue': status.groupvalue, 'statuscaption': status.caption };

        if(callback!=null) callback(_data);
    }

    //rights
    static async rights_init(option:{rightskey:string, stateRights:string}, callback:any=null): Promise<void>{
        let _rights:any = {};
        /*

        if (option.stateRights == 'system-all'){
            Object.getOwnPropertyNames(_rights).forEach(key=>{
                _rights[key] = true;
            });

            if(callback!=null) callback(_rights);
            return;
        }
        */

        await Helper.exec('rights', 'get', {
          loading: true,
          params: { "key": option.rightskey }
        }).then((res:any)=>{
            res.data.map.forEach((item: any) => {
                _rights[item.menukey + "_" + item.code] = false;
            });

            if (option.stateRights == 'system-all'){
                Object.getOwnPropertyNames(_rights).forEach(key=>{
                    _rights[key] = true;
                });
    
                if(callback!=null) callback(_rights);
                return;
            }

            Helper.changeRights(option.stateRights, _rights);

            if (!_rights[option.rightskey.split(",")[0] + "_browse"]){
                _rights = [];
            }

            if(callback!=null) callback(_rights);
        }).catch((err:any)=>{
            Helper.Debug(err, 'rights_init');
        });
    }

    //check right
    static checkright(option:{stateRights:string, rightskey:string, handler:string, rights:Record<string, boolean>}):boolean{
        if(option.stateRights == 'system-all') return true;

        let _handler:string = option.handler.replace(/\-/g, '_');

        if(option.stateRights.replace(/\-/g, '_').indexOf(_handler)>-1){
            if(_handler.split('_').length - 1 > 1){
                return option.rights[option.handler];
            }else{
                return option.rights[`${option.rightskey}_${_handler}`];
            }
        }

        return false;
    }
}

export class orderHelper {
    /*创建订单*/
    static createOrder(option:any){
        option.type = 'pay';
        return DbHelper.set(option);
    }
    /* 发送订单 */
    static payOrder(option:any){
        return DbHelper.set({
            type: 'paybywx',
            params: {'type': 'NATIVE', data: JSON.stringify(option.data) }
        });
    }

    static save(option:any){
        option.type = 'setOrder';
        return DbHelper.set(option);
    }

    static delete(option:any){
        option.type = 'setOrder';
        return DbHelper.delete(option);
    }

    static get(option:any){
        option.type = 'getOrder';
        return DbHelper.get(option);
    }

    static order(option:any){
        commonHelper.sort(option, (data:any)=>{
            DbHelper.set({
                type: option.type,
                params: { type: 'order', data: JSON.stringify(data) }
            }).then((res:any)=>{
                if(option.success!=null) option.success(res);
            }).catch((err:any)=>{
                if(option.fail!=null) option.fail(err);
            });
        });
    }

    static export(option:any){
        option.type = 'exportOrder';
        return DbHelper.download(option);
    }

    static checkPayScan(option:any){
        option.type = 'checkPayScan';
        return DbHelper.set(option);
    }

    //更新状态
    static status(option:any){
        let t:any = option.t;
        
        let _type = 'setOrder';
        let _str = '';

        Helper.Debug(option, '=============更新订单状态======');

        return new Promise((resolve, reject)=>{
            /** 判断车辆是否合法 **/
            if(!option.conf.isLong){
                if(option.conf.Details.length < 1){
                    ElMessage.error({ message: '未发现订单有车辆被派遣，请先派车。'});
                    return;
                }else{
                    //判断核定与需求是否匹配
                    let sum:number = option.conf.Details.reduce((s:number, item:any)=>s + item.num, 0);
                    if(option.order.personnum > 0 && sum < option.order.personnum){
                        ElMessage.error({message: `还有${option.order.personnum - sum}人未分派车辆，无法派车。请继续添加车辆`});
                        return;
                    }
                }
            }

            /** 判断途径点是否合法 **/
            //如果不是长期租车，且无途径点
            if(!option.conf.isLong && option.conf.Paths.length < 1){
                ElMessage.error({ message: '请根据需求填写途径点1111。'});
                return;
            }

            //如果是接送服务
            if(option.conf.isFerry){
                //如果途径点小于两个
                if(option.conf.Paths.length < 2){
                    ElMessage.error({ message: '接送服务要求至少有2个途径点'});
                    return;
                }else{
                    //如果途径点不包括起点或终点
                    if(option.conf.Paths.findIndex((item:any)=>{ return item.typecode == 'start';}) == -1 ||
                        option.conf.Paths.findIndex((item:any)=>{ return item.typecode == 'end';}) == -1){
                        ElMessage.error({ message: '接送服务要求必须包含起点和终点'});
                        return;
                    }
                }
            }

            /** 判断费用 **/
            if(option.statusvalue == 90) {
                //如果状态改为供车方已付清
                if(option.conf.Fees.totaldone == 0 || option.conf.Fees.totalpay == 0 || option.conf.Fees.totaldone != option.conf.Fees.totalprice || option.conf.Fees.totalpay != option.conf.Fees.totalcost){
                    ElMessage.error({ message: '<p>发现订单费用中有未收讫，未付讫或未审核完成的记录，无法更改状态。</p><p>客户费用应全部收讫，向第三方支付应全部付讫，且全部完成审核。请联系财务尽快审核。</p>', duration: 0, dangerouslyUseHTMLString:true, showClose:true, grouping: true});
                    return;
                }
            }else if (option.statusvalue == 80) {
                //如果状态改为客户已付清
                if(option.conf.Fees.totaldone == 0 || option.conf.Fees.totaldone != option.conf.Fees.totalprice){
                    ElMessage.error({ message: '<p>发现订单费用中有未收讫或未审核完成的记录，无法更改状态。</p><p>客户费用应全部收讫，且全部完成审核。请联系财务尽快审核。</p>', duration: 0, dangerouslyUseHTMLString: true, showClose:true, grouping: true});
                    return;
                }
            }else if(option.statusvalue > 10 && option.statusvalue < 80 ) {
              /*
                //如果状态改为正式订单/执行等
                if(option.conf.Fees.totaldone == 0 && option.conf.Fees.noverifydone == 0){
                    ElMessage.error({ message: '订单费用中，至少已提交部分客户费用进行审核或有部分费用已审核。请添加客户支付记录。', duration: 0, showClose:true, grouping: true});
                    return;
                }
              */
            }

            option.smses = [];
            if(option.allowSms){
              switch(Number(option.statusvalue)){
                  case 15:
                  case 20:
                      if(option.statusvalue == 15){
                          _str = '本操作将确认车单信息完备。需要客户确认并支付本车单相关费用后，本车单将自动转为订单生效。';
                      }else{
                          let _tmp = '';
                          if(new Date(option.order.startdatetime).getTime() < new Date().getTime()){
                              _tmp = `<p style="color: red;">注意!!! 本订单生效操作时，发车时间已经过期。请在生效后，再次检查本订单!</p>`;
                          }
                          _str = `${_tmp}<p>本操作将确认订单正式生效。正式生效后，将分别向客户与驾驶员发送短信通知。</p><p>当车辆在起始日期"${Helper.formatDateTime(option.order.startdatetime,'yyyy年MM月dd日 HH:mm')}"开始任务时，如驾驶员遗忘修改状态，您需要立即修改订单状态为：执行中。</p>`;
  
                          option.smses.push({
                              type: SMSType.OrderCreatedByClient,
                              phoneNumbers: option.order.applicantmobile,
                              param: {order: option.order.ordernumber, plate: option.smsParams.plate, num: 0, name: option.smsParams.drivername, phone: option.smsParams.driverphone},
                          });
                          option.smses.push({
                              type: SMSType.OrderCreatedByDirver,
                              phoneNumbers: '',
                              param: {address: option.smsParams.addr, date: Helper.formatDateTime(option.order.startdatetime, 'yyyy-MM-dd HH:mm'), name: option.order.applicant, phone: option.order.applicantmobile},
                          });
                      }
                      break;
                  case 30:
                      _str = '本操作将确认订单开始执行。请确认已选配车辆且车辆已出发。';
                      break;
                  case 70:
                      _str = '本操作将确认此订单下所有车辆均完成客运任务。并等待客户结算。';
                      break;
                  case 80:
                      _str = '本操作将确认客户已结算此订单，并向客户发送短信通知。确认后，将等待向合作方结算。如果无须向合作方结算，请选择“取消”按钮，在工具中选择“订单结算”。';
  
                      if(Helper.getVal(option.conf.money)!=null && option.conf.money > 0){
                          option.smses.push({
                              type: SMSType.OrderDoneByClient,
                              phoneNumbers: option.order.applicantmobile,
                              param: {money: option.conf.money},
                          });
                      }
                      break;
                  case 85:
                      _str = '';
                      break;
                  case 90:
                      _type = 'setOrder';
                      _str = '本操作将确认此订单已结算完成';
                      break;
                  default:
                      ElMessage.error({message: `状态值超出预期。${option.order.statusvalue}`});
                      return;
              }
            }

            commonHelper.status({
                order: option.order, 
                statuslist: option.statuslist,
                statusvalue: option.statusvalue,
            }, (data:any)=>{
                if(_str==''){
                    Helper.exec('order', 'set', {
                        params: {type:'update', data: JSON.stringify(data)}
                    }).then((res:any)=>{
                        ElMessage.success({message: `${t('sys.HANDLE_SUCCESS')}`});
                        resolve(res);
                    }).catch((err:any)=>{
                        reject(err);
                    });
                }else{
                    ElMessageBox.confirm(_str, t('sys.PROPMPT'), {
                        confirmButtonText: t('sys.OK'),
                        cancelButtonText: t('sys.CANCEL'),
                        dangerouslyUseHTMLString: true,
                        type: 'warning',
                    }).then(()=>{
                        Helper.exec('order', 'set', {
                            params: {type:'update', data: JSON.stringify(data)}
                        }).then((res:any)=>{
                            ElMessage.success({message: `${t('sys.HANDLE_SUCCESS')}`});
    
                            /* 发送短信 */
                            /*
                            if(option.smses.length > 0){
                                ElMessage.info({message: `正在向客户或驾驶员发送短信通知...'`});
                                option.smses.forEach((sms:any)=>{
                                    Helper.exec('sms', 'send', {
                                        smstype: sms.conf.type,
                                        phoneNumbers: sms.conf.phoneNumbers,
                                        param: sms.conf.param
                                    }).then((sms:any)=>{
                                        ElMessage.success({message: `发送短信完成!`});
                                    });
                                });
                            }
                            */
                            resolve(res);
                        }).catch((err:any)=>{
                            reject(err);
                        });
                    }).catch(()=>{
                        ElMessage.info({message: t('sys.HANDLE_CANCEL')});
                    });
                }
            });
        });
    }

    static details_save(option:any){
        option.type = 'setOrderdetails';
        return DbHelper.set(option);
    }

    static details_get(option:any){
        option.type = 'getOrderdetails';
        return DbHelper.get(option);
    }

    static details_delete(option:any){
        option.type = 'setOrderdetails';
        return DbHelper.delete(option);
    }

    static verify_get(option:any){
        option.type = 'getVerify';
        return DbHelper.get(option);
    }
    
    static fee_get(option:any){
        option.type = 'getFee';
        return DbHelper.get(option);
    }
    
    static fee_save(option:any){
        option.type = 'setFee';
        return DbHelper.set(option);
    }
    
    static fee_delete(option:any){
        option.type = 'setFee';
        return DbHelper.delete(option);
    }
    
    static path_get(option:any){
        option.type = 'getOrderpaths';
        return DbHelper.get(option);
    }

    static path_save(option:any){
        option.type = 'setOrderpaths';
        return commonHelper.set(option);
    }
    
    static path_delete(option:any){
        option.type = 'setOrderpaths';
        option.params.type = 'delete';
        if(Helper.getVal(option.t)!=null) option.hasAsk = true;
        return DbHelper.delete(option);
    }

    static path_order(option:any){
        option.type = 'setOrderpaths';
        return commonHelper.set(option);
    }

    static offset_get(option:any){
        option.type = 'getOrderOffset';
        return commonHelper.set(option);
    }

    static commission_get(option:any){
        option.type = 'getCommission';
        return DbHelper.get(option);
    }

    static reconciliation_get(option:any){
        option.type = 'getReconciliation';
        return DbHelper.get(option);
    }
}

//file
export class fileHelper {
    static save(option:any){
        option.type = 'setFile';
        return DbHelper.set(option);
    }
    
    static delete(option:any){
        option.type = 'setFile';
        return DbHelper.delete(option);
    }

    static order(option:any){
        option.type = 'setFile';
        commonHelper.sort(option, (data:any)=>{
            ajax.post(Helper.getUrl(option.type, 'set'), {type: 'order', data: JSON.stringify(data)}).then((res:any)=>{
                if(option.success!=null) option.success(res);
            }).catch((err:any)=>{
                if(option.fail!=null) option.fail(err);
            });
        });
    }

    static get(option:any){
        option.type = 'getFile';
        return DbHelper.get(option);
    }

    static getDownloadUrl(option:any){
        let _url:string = '';
        let key:string = option.type??'';
        if(Helper.isUrlKey(key)){
            _url = Helper.getUrl(key);
        }else{
            throw new Error(`API URL not found for key: ${key}`);
        }

        return `${_url}?type=${option.type}&id=${option.id}&name=${option.name}`;
    }

    static download(option:any){
        window.location.href = fileHelper.getDownloadUrl(option);
    }
}

export class searchHelper {
    static get(store:Store<any>, key:any=null){
        let _data:any = store.state.search.data;

        if(Helper.getVal(_data)!=null){
            if(Helper.isJson(_data)){
                let _json:any = JSON.parse(_data);

                if(key!=null){
                    if(_json.hasOwnProperty(key)){
                        return _json[key];
                    }
                }

                return [];
            }
        }else{
            return [];
        }
    }

    static save(store:Store<any>, key:any, id:string, list:Array<any>){
        if(Helper.getVal(list)==null || list.length < 1) return;

        let obj:any = null;
        list.forEach((item:any)=>{
            if(item.hasOwnProperty('options')){
                let _obj:any = item.options.find((el:any)=>{ return el.id == id;});
                if(_obj!=null){
                    obj = _obj;
                    return;
                }
            }else{
                if(item.id == id){
                    obj = item;
                    return;
                }
            }
        });

        if(obj == null) return;

        searchHelper.set(store, key, obj);
    }

    static set(store:Store<any>, key:any, value:any){
        /*
        value:
            {id: 'c66505114fba47978ed389c3b04c07ee', caption: '小凡老师', type: 'member'}
            {id: '943ed4f830c44291b5399b079305e266', mobile: '18521015067', fullname: '王阳', dutycaption: '初级业务员', departmentcaption: '业务部'}
        */
        let _json:any = store.getters['search/getSearch'];

        let _caption:string = '';
        switch(value.type){
            case 'member':
                _caption = '个人用户';
                break;
            case 'enterprise':
                _caption = '企业用户';
                break;
        }

        if(!_json.hasOwnProperty(key)){
            if(_caption!=''){
                _json[key] = [{caption: _caption, options: []}];
            }else{
                _json[key] = [];
            }
            
        }
        let _data:any = _json[key];

        if(!value.hasOwnProperty('type')){
            if(_data.find((item:any)=>{ return item.id == value.id})!=null) return;
            
            _data.push(value);

            //数组超过5个，删除第一个
            if(_data.length > 5){
                _data.shift();
            }
        }else{

            let _index:number = _data.findIndex((item:any)=>{ return item.caption == _caption; });

            if(_index > -1){
                if(_data[_index].options.find((item:any)=>{return item.id == value.id;})!=null) return;
                _data[_index].options.push(value);

                //数组超过5个，删除第一个
                if(_data[_index].options.length > 5){
                    _data[_index].options.shift();
                }
            }else{
                _data.push({caption: _caption, options:[value]});
            }
        }
        _json[key] = _data;

        store.dispatch('search/SEARCH_SAVE', _json);
    }

    static remove(store:Store<any>, key:any){
        let _json:any = store.getters['search/getSearch'];

        if(!_json.hasOwnProperty(key)){
            delete _json[key];
        }

        store.dispatch('search/SEARCH_SAVE', _json);
    }
}

export class storageHelper {
    static order_get(store:Store<any>){
        let _data:any = store.getters['order/getOrderinfo'];

        return _data;
    }

    static order_save(store:any, value:any){
        store.dispatch('order/INFO_SAVE', value);
    }
}

export enum SMSType
{
    /**
     * 您的用车已变更。车辆：${plate}-核定${num}+1座，司机：${name}-手机${phone}
     */
    CarChangeByClient = 'SMS_475835028',
    /**
     * 您的用车所属司机已变更。车辆：${plate}-核定${num}+1座，司机：${name}-手机${phone}
     */
    DirverChangeByClient = 'SMS_475745050',
    /**
     * 亲，感谢您选用嘉腾巴士！为您提供下次用车优惠${money}元，已绑定您本次的手机号，长期有效。
     */
    OrderDoneByClient = 'SMS_475110198',
    /**
     * 订单${order}生效！车牌${plate}-核定${num}+1座；司机：${name}-电话：${phone}
     */
    OrderCreatedByClient = 'SMS_475125174',
    /**
     * 新工单！自${address}于${date}发车；客户：${name}；电话：${phone}
     */
    OrderCreatedByDirver = 'SMS_475165168',
    /**
     * 待付款：${all_money}元；其中：尾款 ${last_money}元；超时 ${t_money}元；超程 ${m_money}元；杂项 ${o_money}元
     */
    OrderPayByClient = 'SMS_475020833',
    /**
     * 验证码为：${code}，您正在登录平台账号，若非本人操作，请忽略本短信。
     */
    VerifyCodeByLogin = 'SMS_474825843',
    /**
     * 您的验证码为：${code}，请勿泄露于他人
     */
    VerifyCodeByCommon = 'SMS_305519114',
}
/**
 * 短信发送
 */
export class smsHelper {
    /**
     * 发送短信
     * @param options 
     * options.smsType: SMSType
     * options.param: JSON
     * options.phoneNumbers: '139xxxxx,129xxxxxx'
     * @returns Promise
     */
    static send(options:any):Promise<any>{
        if(!smsHelper.checkParam(options.param, options.smstype)){
            return new Promise((resolve, reject)=>{
                reject('error');
            });
        }

        return Helper.exec('sms', 'send', {
            params: {
              type: 'send',
              data: JSON.stringify({
                  templateCode: options.smstype,
                  templateParam: options.param,
                  phoneNumbers: options.phoneNumbers
              })
          }
        });
    }

    static checkParam(param:any, t:SMSType):boolean{
        let err:string = '';
        switch(t){
            case SMSType.VerifyCodeByLogin:
            case SMSType.VerifyCodeByCommon:
                if(param.keys.length!=1){
                    err = '参数个数错误';
                }else if(!param.hasOwnProperty('code')){
                    err = '参数不包括 code';
                }
                break;
            case SMSType.CarChangeByClient:
            case SMSType.DirverChangeByClient:
                if(param.keys.length!=4){
                    err = '参数个数错误';
                }else{
                    if(!param.hasOwnProperty('plate')) err += '参数不包括 plate;';
                    if(!param.hasOwnProperty('num')) err += '参数不包括 num;';
                    if(!param.hasOwnProperty('name')) err += '参数不包括 name;';
                    if(!param.hasOwnProperty('phone')) err += '参数不包括 phone;';
                }
                break;
            case SMSType.OrderCreatedByClient:
                if(param.keys.length!=5){
                    err = '参数个数错误';
                }else{
                    if(!param.hasOwnProperty('order')) err += '参数不包括 order;';
                    if(!param.hasOwnProperty('plate')) err += '参数不包括 plate;';
                    if(!param.hasOwnProperty('num')) err += '参数不包括 num;';
                    if(!param.hasOwnProperty('name')) err += '参数不包括 name;';
                    if(!param.hasOwnProperty('phone')) err += '参数不包括 phone;';
                }
                break;
            case SMSType.OrderCreatedByDirver:
                if(param.keys.length!=4){
                    err = '参数个数错误';
                }else{
                    if(!param.hasOwnProperty('address')) err += '参数不包括 address;';
                    if(!param.hasOwnProperty('date')) err += '参数不包括 date;';
                    if(!param.hasOwnProperty('name')) err += '参数不包括 name;';
                    if(!param.hasOwnProperty('phone')) err += '参数不包括 phone;';
                }
                break;
            case SMSType.OrderDoneByClient:
                if(param.keys.length!=1){
                    err = '参数个数错误';
                }else{
                    if(!param.hasOwnProperty('money')) err += '参数不包括 money;';
                }
                break;
            case SMSType.OrderPayByClient:
                if(param.keys.length!=5){
                    err = '参数个数错误';
                }else{
                    if(!param.hasOwnProperty('all_money')) err += '参数不包括 all_money;';
                    if(!param.hasOwnProperty('last_money')) err += '参数不包括 last_money;';
                    if(!param.hasOwnProperty('t_money')) err += '参数不包括 t_money;';
                    if(!param.hasOwnProperty('m_money')) err += '参数不包括 m_money;';
                    if(!param.hasOwnProperty('o_money')) err += '参数不包括 o_money;';
                }
                break;
        }
        if(err!=''){
            ElMessage.error({message: err});
            return false;
        }

        return true;
    }
}

interface KeyPair {
  publicKey: string;
  privateKey: string;
  expiresAt: number; // 必须字段，默认8小时
}

export class shareSystemCrypto {
  private static DEFAULT_EXPIRES_SEC = 8 * 60 * 60; // 默认8小时
  private static keyPairCache: Map<string, KeyPair> = new Map();

  /**
   * 生成或获取缓存的密钥对
   */
  static getKeyPair(validSeconds: number = this.DEFAULT_EXPIRES_SEC): KeyPair {
    const cacheKey = `keypair_${validSeconds}`;
    
    if (this.keyPairCache.has(cacheKey)) {
      const cached = this.keyPairCache.get(cacheKey)!;
      // 检查缓存是否过期
      if (cached.expiresAt > Date.now() / 1000) {
        return cached;
      }
      this.keyPairCache.delete(cacheKey);
    }
    
    const newKeyPair = this.generateKeyPair(validSeconds);
    this.keyPairCache.set(cacheKey, newKeyPair);
    return newKeyPair;
  }
  
  /**
   * 生成 RSA 签名
   * @param params 请求参数
   * @param privateKey 用于签名的私钥
   */
  private static generateSignature(params: any, privateKey: string): string | false {
    const sortedParams = JSON.stringify(params, Object.keys(params).sort());
    
    const signer = new JSEncrypt();
    signer.setPrivateKey(privateKey);
    
    // 使用 SHA-256 签名
    return signer.signSha256(sortedParams);
}

  /**
   * 生成密钥对
   * @param validSeconds 有效期（秒），不传则默认8小时
   */
  static generateKeyPair(validSeconds: number = this.DEFAULT_EXPIRES_SEC): KeyPair {
    const encryptor = new JSEncrypt({ default_key_size: '2048' });
    return {
      publicKey: encryptor.getPublicKey()!,
      privateKey: encryptor.getPrivateKey()!,
      expiresAt: Math.floor(Date.now() / 1000) + validSeconds
    };
  }

  /**
   * 发起安全请求（支持部分内容加密）
   */
  static async secureRequest(params: any) {
    const { publicKey, privateKey } = this.getKeyPair();
    const signature = this.generateSignature(params, privateKey);

    const cleanPublicKey = publicKey.replace(/\n/g, '')  // 移除所有换行符
      .replace(/\r/g, '');         // 移除回车符

    
    try {
      const response: any = await Helper.exec('secure', 'set', {
        params: params,
      }, {
        headers: {
          'X-JiatengBus-Public': cleanPublicKey,
          'X-Timestamp': Date.now(),
          'X-Signature': signature
        }
      });

      // 解密后端返回的加密字段
      const decryptedData: any = {};
      if (response.data.encrypted) {
        const decryptor = new JSEncrypt();
        decryptor.setPrivateKey(privateKey);

        Object.keys(response.data.encrypted).forEach((field:string) => {
          const decrypted = decryptor.decrypt(response.data.encrypted[field]);
          if (decrypted) {
              try {
                  decryptedData[field] = JSON.parse(decrypted);
              } catch (e) {
                  // 如果解析失败，直接使用原始字符串
                  decryptedData[field] = decrypted;
                  console.warn(`字段 ${field} 不是JSON格式，使用原始值`);
              }
          }
        });
      }

      // 返回数据：明文数据 + 解密后的数据
      return {
        ...response.data.data, // 非加密数据
        ...decryptedData       // 解密后的数据
      };
    } catch (error) {
      console.error('安全请求失败:', error);
      throw error;
    }
  }
}

export default Helper